/**
 * chatlist.ts — standalone chat list widget.
 *
 * Completely separate from mount() / the chat widget.
 * Shows all conversations for a given userId / guest on a profile.
 * Tapping a row fires onSelect(entry) — the caller decides what to do
 * (navigate to a new page, open a ChatWidget inline, etc.)
 *
 * Usage (vanilla):
 *   import { mountChatList } from '@paramms/chat-widget/chatlist'
 *   const handle = mountChatList({
 *     el: document.getElementById('chat-list'),
 *     url: 'https://api.relay.paramms.com',   // ONE url, any scheme
 *     profileId: 'p_usedcars',
 *     userId: currentUser.id,          // optional — uses localStorage UID if omitted
 *     onSelect: (entry) => {
 *       window.location.href = `/listings/${entry.subjectId}#chat`
 *     },
 *   })
 *   handle.refresh()   // manually re-fetch the list
 *   handle.close()     // unmount and clean up
 */

import { resolveRelayUrls } from './history.js'
import { CSS } from './chatlist.styles.js'
import { resolveStrings } from './i18n.js'
import { persistentUid } from './uid.js'
import { encodeFrame, decodeFrame } from './protocol/codec.js'

export interface ChatListEntry {
  id:            string
  /** Chatroom this conversation belongs to. With `scope: 'tenant'` this can
   *  differ from the profileId the list was mounted with — open the chat
   *  against THIS profileId. */
  profileId?:    string
  /** 'support' (default) or 'direct' (user↔user DM). */
  kind?:         string
  /** For direct conversations: the other participant's user id. */
  peerId?:       string
  subjectId?:    string
  subjectTitle?: string
  /** One-line detail — e.g. "45,000 km · Auto" */
  subjectMeta?:  string
  /** URL of the listing/item page — stored automatically when the widget first opens */
  subjectUrl?:   string
  state:         string
  updatedAt:     number
  lastSeq?:      number
  lastMessage?:  string
}

export interface ChatListOptions {
  /** Mount target element */
  el:           HTMLElement
  /** Relay URL — ONE url, any scheme (https recommended). The WebSocket URL
   *  and REST base are derived automatically. */
  url:          string
  /** HTTP(S) base for REST — only when REST is on a different origin.
   *  @deprecated pass a single `url`; kept for back-compat. */
  apiUrl?:      string
  /** Profile ID to scope conversations to */
  profileId?:   string
  /** Tenant-level identification — list EVERY conversation this user has
   *  with the business, across ALL of its chatrooms, without naming one
   *  (e.g. a platform running a marketplace chatroom AND a general-support
   *  chatroom). Provide `profileId` OR `tenantId` (profileId wins if both;
   *  it also fixes where "new conversation" opens). With only `tenantId`,
   *  the compose target is the server-reported defaultProfileId (the
   *  tenant's oldest chatroom). */
  tenantId?:    string
  /** A signed identity token (ES256 JWT) — the production identity tier for
   *  chatrooms with signed identity enabled. Wins over `userId`. */
  token?:       string
  /** Your logged-in user's stable ID. Omit for anonymous (uses localStorage UID) */
  userId?:      string
  /** Which conversations to list (default 'profile'):
   *    'profile' — only this chatroom's threads.
   *    'tenant'  — every conversation this user has with the chatroom's owning
   *      business, across ALL of its chatrooms (a real chat-app inbox). Rows
   *      carry `profileId` so each opens against the right chatroom. */
  scope?:       'profile' | 'tenant'
  /** Called when the user taps a conversation row */
  onSelect:     (entry: ChatListEntry) => void
  /** When provided, the list shows a ✎ compose button in the header (and a
   *  "Start a conversation" button in the empty state) that calls this —
   *  wire it to open a fresh/general thread. Without it a user with no
   *  conversations yet has nothing to tap. */
  onNewChat?:   () => void
  /** Set when the CALLER draws its own close (✕) control overlaying the list's
   *  top-right corner — reserves header space so it doesn't sit on top of the
   *  ✎ compose button. */
  reserveCloseSpace?: boolean
  /** Brand colour hex — default '#6c5ce7' */
  accent?:      string
  theme?:       'auto' | 'light' | 'dark'
  webfont?:     boolean
  /** UI language for built-in strings ('ko', 'ko-KR', …). Omit to auto-detect
   *  from the visitor's browser. Korean ⇄ English; non-Korean → English. */
  locale?:      string
  /** i18n overrides */
  i18n?: {
    title?:      string   // default 'Your conversations'
    search?:     string   // default '🔍  Search'
    empty?:      string   // default 'No conversations yet.'
    unread?:     string   // default 'Unread'
    all?:        string   // default 'All conversations'
    error?:      string   // default 'Could not load conversations.'
    retry?:      string   // default 'Retry'
    close?:      string   // default 'Close' (aria-label for the ✕ control)
    newChat?:    string   // compose button, default 'New conversation'
    startChat?:  string   // empty-state CTA, default 'Start a conversation'
  }
}

export interface ChatListHandle {
  /** Re-fetch and re-render the list */
  refresh(): void
  /** Unmount and clean up */
  close(): void
  /** Where "new conversation" should open: the configured profileId, else the
   *  server-reported tenant default (oldest chatroom). Undefined until the
   *  first successful fetch when only tenantId was configured. */
  defaultProfileId(): string | undefined
}

/** Map a conversation state to a status chip (label + style class). Returns null
 *  for states with no meaningful badge. */
function statusChip(state: string): { label: string; cls: string } | null {
  switch (state) {
    case 'open':          return { label: 'Open', cls: 'open' }
    case 'awaiting_staff':return { label: 'Waiting on you', cls: 'waiting' }
    case 'resolved':      return { label: 'Resolved', cls: 'done' }
    case 'closed':        return { label: 'Closed', cls: 'done' }
    default:              return null
  }
}

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  const days = Math.floor(s / 86400)
  if (days <= 7) return `${days}d`
  // Beyond a week, a date scans better than "43d" (what Channel.io/Intercom do).
  try { return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }
  catch { return `${days}d` }
}

// CSS lives in chatlist.styles.ts (single source of truth for .ocl styling).

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  if (text !== undefined) e.textContent = text
  return e
}

// CSS lives in chatlist.styles.ts (single source of truth for .ocl styling).

/** Mount a standalone chat list widget. */
export function mountChatList(opts: ChatListOptions): ChatListHandle {
  const token = opts.token ?? opts.userId ?? persistentUid()
  const { httpBase, wsUrl } = resolveRelayUrls(opts.url, opts.apiUrl)
  const i18n = resolveStrings(opts.locale, opts.i18n)
  const accent = opts.accent ?? '#6c5ce7'

  // Inject the shared stylesheet ONCE, and keep it accent-FREE. The accent was
  // previously baked into this shared <style> (CSS.replace(/#f5713c/g, accent)),
  // which meant the FIRST list mounted on a page won the accent for EVERY list
  // after it (the style tag already existed, so a second list's colour was
  // ignored) — real interference when a project runs more than one widget. The
  // accent now lives in a per-instance CSS variable set on the root element
  // below, so each list keeps its own colour and the shared sheet stays static
  // (also friendlier to HTTP caching).
  if (!document.getElementById('ocl-styles')) {
    const s = document.createElement('style'); s.id = 'ocl-styles'
    s.textContent = CSS
    document.head.append(s)
  }
  // Brand webfonts (shared id with the chatroom, injected once). Opt out with webfont:false.
  if (opts.webfont !== false && typeof document !== 'undefined' && !document.getElementById('ocw-webfont')) {
    const l = document.createElement('link')
    l.id = 'ocw-webfont'; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700&family=Nunito:wght@400;500;600;700&display=swap'
    document.head.append(l)
  }

  // Build DOM
  const root = el('div', 'ocl')
  root.style.setProperty('--ocl-accent', accent)   // per-instance accent
  // Colour scheme is opt-in, never OS-auto-detected by default (see renderer.ts):
  // unset → 'light'; 'auto' follows the OS; 'dark'/'light' force it.
  root.dataset.theme = opts.theme ?? 'light'
  if (opts.reserveCloseSpace) root.classList.add('ocl-has-close')
  const head = el('div', 'ocl-head')
  // Header shows the list title as text (the redesign's "Your conversations"
  // heading), host-overridable via i18n.title.
  const titleText = i18n.title ?? 'Your conversations'
  const titleEl = el('span', 'ocl-title', titleText)
  titleEl.setAttribute('aria-label', titleText)
  head.append(titleEl)
  if (opts.onNewChat) {
    const compose = el('button', 'ocl-compose', '✎') as HTMLButtonElement
    compose.title = i18n.newChat ?? 'New conversation'
    compose.addEventListener('click', () => opts.onNewChat!())
    head.append(compose)
  }

  const searchWrap = el('div', 'ocl-search-wrap')
  const searchIn = el('input', 'ocl-search') as HTMLInputElement
  searchIn.placeholder = i18n.search ?? '🔍  Search'; searchIn.type = 'search'
  searchWrap.append(searchIn)

  const body = el('div', 'ocl-body')
  body.append(el('div', 'ocl-spinner', 'Loading…'))
  root.append(head, searchWrap, body)
  opts.el.replaceChildren(root)

  // Container-driven compact sizing (see the CSS note). Guarded for
  // jsdom/old runtimes without ResizeObserver — they keep desktop sizing.
  const applyCompact = (w: number): void => { root.classList.toggle('ocl-compact', w > 0 && w < 400) }
  applyCompact(root.clientWidth)
  let compactObserver: ResizeObserver | null = null
  if (typeof ResizeObserver !== 'undefined') {
    compactObserver = new ResizeObserver((entries) => applyCompact(entries[0]?.contentRect.width ?? root.clientWidth))
    compactObserver.observe(root)
  }

  // Track seen seqs for unread counts (persisted in localStorage)
  // Key unread tracking by the STABLE id (userId beats token here: a signed
  // JWT changes every mint, which would reset unread counts on each load).
  if (!opts.profileId && !opts.tenantId) throw new Error('[relay chatlist] provide profileId or tenantId')
  let serverDefaultProfileId: string | undefined
  const seenKey = `ocl_seen_${opts.profileId ?? `t_${opts.tenantId}`}_${(opts.userId ?? token).slice(-8)}`
  let seenSeq: Record<string, number> = {}
  try { seenSeq = JSON.parse(localStorage.getItem(seenKey) ?? '{}') } catch {}

  const saveSeenSeq = () => {
    try { localStorage.setItem(seenKey, JSON.stringify(seenSeq)) } catch {}
  }

  let allEntries: ChatListEntry[] = []
  let destroyed = false

  // Fetch conversations from server
  const fetchEntries = async (): Promise<ChatListEntry[]> => {
    const who = opts.profileId
      ? `profileId=${encodeURIComponent(opts.profileId)}${opts.scope === 'tenant' ? '&scope=tenant' : ''}`
      : `tenantId=${encodeURIComponent(opts.tenantId!)}`   // tenant-level is inherently tenant-scoped
    const url = `${httpBase}/conversations/mine?${who}`
    const headers = { authorization: `Bearer ${token}` }
    // A GET is safe to repeat. A single transient network reject — the socket
    // still warming up right after a reload, a relay that blipped — used to
    // dead-end straight to "Could not load conversations." Retry once so a blip
    // self-heals; an HTTP error (4xx/5xx) is NOT a network failure and returns
    // an empty list rather than retrying or erroring.
    let res: Response
    try {
      res = await fetch(url, { headers })
    } catch {
      res = await fetch(url, { headers })   // one immediate retry
    }
    if (!res.ok) return []
    const data = await res.json() as { conversations?: ChatListEntry[]; defaultProfileId?: string }
    if (data.defaultProfileId) serverDefaultProfileId = data.defaultProfileId
    return (data.conversations ?? []).sort((a, b) => b.updatedAt - a.updatedAt)
  }

  // Render rows from entries, optionally filtered by search query
  const renderRows = (entries: ChatListEntry[], query: string) => {
    if (destroyed) return
    const filtered = query
      ? entries.filter(e =>
          rowName(e).toLowerCase().includes(query) ||
          (e.lastMessage ?? '').toLowerCase().includes(query)
        )
      : entries

    body.replaceChildren()

    if (!filtered.length) {
      const empty = el('div', 'ocl-empty', query ? 'No results.' : (i18n.empty ?? 'No conversations yet.'))
      if (!query && opts.onNewChat) {
        empty.append(el('br'))
        const start = el('button', 'ocl-start', i18n.startChat ?? 'Start a conversation') as HTMLButtonElement
        start.addEventListener('click', () => opts.onNewChat!())
        empty.append(start)
      }
      body.append(empty)
      return
    }

    const isUnread = (e: ChatListEntry) =>
      (e.lastSeq ?? 0) > (seenSeq[e.id] ?? 0)

    const unread = filtered.filter(isUnread)
    const read   = filtered.filter(e => !isUnread(e))

    if (unread.length) {
      body.append(el('div', 'ocl-section', `${i18n.unread ?? 'Unread'} (${unread.length})`))
      for (const e of unread) body.append(buildRow(e, isUnread(e)))
    }
    if (read.length) {
      body.append(el('div', 'ocl-section', unread.length ? (i18n.all ?? 'All conversations') : ''))
      for (const e of read) body.append(buildRow(e, false))
    }
  }

  const rowName = (entry: ChatListEntry): string =>
    entry.subjectTitle ?? (entry.kind === 'direct' ? (entry.peerId ?? 'Direct message') : 'General enquiry')

  const buildRow = (entry: ChatListEntry, unread: boolean): HTMLElement => {
    const name = rowName(entry)
    // Prefer the first LETTER (any script), not a leading digit/symbol — a
    // listing titled "2018 Kia K7" should show "K", not "2", and Korean/other
    // scripts pick their first character too.
    const initial = (name.match(/\p{L}/u)?.[0] ?? name.trim()[0] ?? '?').toUpperCase()
    const lastSeq = entry.lastSeq ?? 0
    const unreadCount = unread ? Math.max(1, lastSeq - (seenSeq[entry.id] ?? 0)) : 0

    const row = el('button', `ocl-row${unread ? ' unread' : ''}`) as HTMLButtonElement

    // Avatar
    const av = el('div', 'ocl-av', initial)
    row.append(av)

    // Info
    const info = el('div', 'ocl-info')
    info.append(el('div', 'ocl-name', name))
    const stateMap: Record<string, string> = {
      open: 'Open', awaiting_staff: 'Waiting for reply…',
      resolved: 'Resolved ✓', closed: 'Closed',
    }
    info.append(el('div', 'ocl-preview', entry.lastMessage ?? stateMap[entry.state] ?? entry.state))
    row.append(info)

    // Right: timestamp, status chip, unread badge
    const right = el('div', 'ocl-right')
    right.append(el('div', 'ocl-time', timeAgo(entry.updatedAt)))
    const chip = statusChip(entry.state)
    if (chip) right.append(el('div', `ocl-status ${chip.cls}`, chip.label))
    if (unreadCount > 0) {
      right.append(el('div', 'ocl-badge', String(unreadCount > 99 ? '99+' : unreadCount)))
    }
    row.append(right)

    row.addEventListener('click', () => {
      // Mark as read
      if (lastSeq > 0) { seenSeq[entry.id] = lastSeq; saveSeenSeq() }
      row.classList.remove('unread')
      right.querySelector('.ocl-badge')?.remove()
      opts.onSelect(entry)
    })

    return row
  }

  const refresh = () => {
    if (destroyed) return
    fetchEntries().then(entries => {
      if (destroyed) return
      allEntries = entries
      renderRows(entries, searchIn.value.trim().toLowerCase())
    }).catch((e) => {
      if (destroyed) return
      console.error(`[chat-widget] failed to load conversations from ${httpBase}/conversations/mine — check the apiUrl/CORS config.`, e)
      const errBox = el('div', 'ocl-empty', i18n.error ?? 'Could not load conversations.')
      errBox.append(el('br'))
      const retry = el('button', 'ocl-retry', i18n.retry ?? 'Retry') as HTMLButtonElement
      retry.addEventListener('click', () => {
        body.replaceChildren(el('div', 'ocl-spinner', 'Loading…'))
        refresh()
      })
      errBox.append(retry)
      body.replaceChildren(errBox)
    })
  }

  searchIn.addEventListener('input', () => renderRows(allEntries, searchIn.value.trim().toLowerCase()))

  // Initial fetch
  refresh()

  // ── Live inbox: subscribe over WS so the list updates the instant any of the
  // guest's conversations changes, instead of only on the periodic poll. The
  // server streams `inbox_event` to the guest's OWN inbox (keyed by guestId).
  // We coalesce bursts and re-fetch (the fetch already sorts/dedupes); the poll
  // stays as a backstop for a dropped socket. ────────────────────────────────
  let sock: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined
  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let connectedBefore = false
  const debouncedRefresh = () => {
    if (refreshTimer) return
    refreshTimer = setTimeout(() => { refreshTimer = undefined; refresh() }, 300)
  }
  const connectInbox = () => {
    if (destroyed) return
    try { sock = new WebSocket(wsUrl) } catch { scheduleReconnect(); return }
    sock.binaryType = 'arraybuffer'
    sock.onopen = () => {
      attempt = 0
      sock!.send(encodeFrame({ type: 'auth', token }))
      sock!.send(encodeFrame({ type: 'subscribe_inbox' }))
      // On a RECONNECT (not the first connect — the initial mount already
      // fetched), catch up on anything that changed while the socket was down.
      if (connectedBefore) debouncedRefresh()
      connectedBefore = true
    }
    sock.onmessage = (ev) => {
      const frame = decodeFrame(new Uint8Array(ev.data as ArrayBuffer))
      // Any inbox change for this guest → refresh the list. `new` (a freshly
      // created thread) and `update` (new message / state) both apply.
      if (frame && frame.type === 'inbox_event') debouncedRefresh()
    }
    sock.onclose = () => { sock = null; scheduleReconnect() }
    sock.onerror = () => { try { sock?.close() } catch { /* noop */ } }
  }
  const scheduleReconnect = () => {
    if (destroyed || reconnectTimer) return
    const delay = Math.min(15_000, 500 * 2 ** attempt++) + Math.random() * 250
    reconnectTimer = setTimeout(() => { reconnectTimer = undefined; connectInbox() }, delay)
  }
  connectInbox()

  return {
    refresh,
    close() {
      destroyed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (refreshTimer) clearTimeout(refreshTimer)
      compactObserver?.disconnect()
      compactObserver = null
      try { sock?.close() } catch { /* noop */ }
      sock = null
      opts.el.replaceChildren()
    },
    defaultProfileId() { return opts.profileId ?? serverDefaultProfileId },
  }
}
